import React, { useEffect, useState } from "react";
import Select from "react-select";
import { getSupplierData } from "../service/customerService";
import { getMedicineData } from "../service/medicineService";
import { createOrder } from "../service/inventoryService";

const NewPurchase = () => {
  const lsUserVO = process.env.REACT_APP_LS_USER_VO;
  const pimsUserVO = JSON.parse(localStorage.getItem(lsUserVO));
  const [orderDate, setOrderDate] = useState("");
  const [supplier, setSupplier] = useState(null);
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [newSupplier, setNewSupplier] = useState({
    name: "",
    address: "",
    contactNumber: "",
  });
  const [medicineItems, setMedicineItems] = useState([
    {
      medicineId: "",
      qty: "",
      mrp: "",
      finalPrice: "",
      newMed: false,
      newMedName: "",
      total: 0,
    },
  ]);
  const [error, setError] = useState("");

  const fetchMedicineData = async () => {
    try {
      const medicineData = await getMedicineData(pimsUserVO.branchId);
      setMedicineOptions(medicineData);
    } catch (error) {
      console.error("Error fetching medicine data:", error);
    }
  };

  const fetchSupplierData = async () => {
    try {
      const supplierData = await getSupplierData(pimsUserVO.orgId);
      setSuppliers(supplierData);
    } catch (error) {
      console.error("Error fetching supplier data:", error);
    }
  };

  useEffect(() => {
    fetchMedicineData();
    fetchSupplierData();
  }, []);

  const handleAddItem = () => {
    setMedicineItems([
      ...medicineItems,
      {
        medicineId: "",
        qty: "",
        mrp: "",
        finalPrice: "",
        newMed: false,
        newMedName: "",
        total: 0,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = medicineItems.filter(
      (_, itemIndex) => itemIndex !== index
    );
    setMedicineItems(newItems);
    calculateTotalAmount(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...medicineItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    if (field === "finalPrice" || field === "qty") {
      const qty = parseFloat(newItems[index].qty) || 0;
      const finalPrice = parseFloat(newItems[index].finalPrice) || 0;
      newItems[index].total = qty * finalPrice;
    }

    if (field === "medicineId" && value === "New Medicine") {
      newItems[index].newMed = true;
    } else if (field === "medicineId") {
      newItems[index].newMed = false;
    }

    setMedicineItems(newItems);
    calculateTotalAmount(newItems);
  };

  const handleSupplierChange = (selectedOption) => {
    if (selectedOption.value === "New Supplier") {
      setIsNewSupplier(true);
      setSupplier({ value: "New Supplier", label: "New Supplier" });
    } else {
      setIsNewSupplier(false);
      setSupplier(selectedOption);
    }
  };

  const handleNewSupplierChange = (field, value) => {
    setNewSupplier({ ...newSupplier, [field]: value });
  };

  const resetFields = () => {
    setOrderDate("");
    setSupplier(null);
    setIsNewSupplier(false);
    setNewSupplier({
      name: "",
      address: "",
      contactNumber: "",
    });
    setMedicineItems([
      {
        medicineId: "",
        qty: "",
        mrp: "",
        finalPrice: "",
        newMed: false,
        newMedName: "",
        total: 0,
      },
    ]);
    setError("");
    setTotalAmount(0);
  };

  const calculateTotalAmount = (items) => {
    const total = items.reduce((acc, item) => acc + (item.total|| 0), 0);
    setTotalAmount(total);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const processedSupplier = isNewSupplier ? newSupplier : supplier;
    const hasMedicine = medicineItems.some((item) => item.medicineId !== "");
    if (isNewSupplier) {
      if (!newSupplier.name) {
        setError("Please enter a new supplier name.");
        return;
      }
      if (!newSupplier.address) {
        setError("Please enter a new supplier address.");
        return;
      }
      if (!newSupplier.contactNumber) {
        setError("Please enter a new supplier contact number.");
        return;
      }
    } else if (!supplier) {
      setError("Please select a supplier.");
      return;
    }

    if (!hasMedicine) {
      setError("Please select at least one medicine.");
      return;
    }

    setError(""); // Clear any existing errors

    const processedMedicineItems = medicineItems.map((item) => ({
      ...item,
      medicineId: item.medicineId === "New Medicine" ? null : item.medicineId,
    }));

    const orderDTO = {
      orderDate: orderDate,
      customerId: processedSupplier.value,
      newCustomer: isNewSupplier,
      customerDTO: isNewSupplier
        ? {
            name: newSupplier.name,
            address: newSupplier.address,
            contactNumber: newSupplier.contactNumber,
            customerType: "SUPPLIER",
            orgId: pimsUserVO.orgId,
          }
        : null,
      orderType: "PURCHASE_ORDER",
      orderItemDTO: processedMedicineItems,
      branchId: pimsUserVO.branchId,
    };

    try {
      const response = await createOrder(orderDTO);
      if (response && response.status === 201) {
        resetFields();
      } else {
        setError("Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      setError("Failed to create order. Please try again.");
    }
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: 52, // Adjust the height here
    }),
  };

  return (
    <div className="container mx-auto p-4 sm:p-8">
      <div className="max-w-4xl mx-auto bg-green-200 p-6 sm:p-8 rounded-md shadow-md">
        <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
          Purchase Order
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4 sm:mb-6">
            <label
              className="block text-lg font-medium mb-2 sm:mb-3"
              htmlFor="orderDate"
            >
              Order Date
            </label>
            <input
              className="w-full p-2 sm:p-3 border border-gray-300 rounded-md text-lg"
              type="date"
              id="orderDate"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              required
            />
          </div>
          <div className="mb-4 sm:mb-6">
            <label
              className="block text-lg font-medium mb-2 sm:mb-3"
              htmlFor="supplier"
            >
              Supplier
            </label>
            <Select
              className="w-full text-lg"
              value={supplier}
              onChange={handleSupplierChange}
              options={[
                ...suppliers.map((supp) => ({
                  value: supp.id,
                  label: `${supp.name}, ${supp.address}, ${supp.contactNumber}`,
                })),
                { value: "New Supplier", label: "New Supplier" },
              ]}
              placeholder="Select Supplier"
              isSearchable
              required
            />
            {isNewSupplier && (
              <div className="mt-2">
                <input
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2"
                  type="text"
                  placeholder="New Supplier Name"
                  value={newSupplier.name}
                  onChange={(e) =>
                    handleNewSupplierChange("name", e.target.value)
                  }
                  required
                />
                <input
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2"
                  type="text"
                  placeholder="New Supplier Address"
                  value={newSupplier.address}
                  onChange={(e) =>
                    handleNewSupplierChange("address", e.target.value)
                  }
                  required
                />
                <input
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-md text-lg"
                  type="text"
                  placeholder="Contact Number"
                  value={newSupplier.contactNumber}
                  onChange={(e) =>
                    handleNewSupplierChange("contactNumber", e.target.value)
                  }
                  required
                />
              </div>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4">
            Medicine Items
          </h3>
          {medicineItems.map((item, index) => (
            <div key={index} className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:space-x-3 mb-2 sm:mb-3">
                <Select
                  className="w-full sm:w-1/3 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                  styles={customStyles}
                  value={
                    item.medicineId
                      ? medicineOptions.find(
                          (opt) => opt.id === item.medicineId
                        ) || { id: "New Medicine", medName: "New Medicine" }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleItemChange(
                      index,
                      "medicineId",
                      selectedOption ? selectedOption.id : ""
                    )
                  }
                  options={[
                    ...medicineOptions,
                    { id: "New Medicine", medName: "New Medicine" },
                  ]}
                  getOptionLabel={(option) => option.medName}
                  getOptionValue={(option) => option.id}
                  placeholder="Select Medicine"
                  isSearchable
                  required
                />
                {item.newMed && (
                  <div className="w-full sm:w-1/3 sm:mt-0">
                    <input
                      className="w-full p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                      type="text"
                      placeholder="Medicine Name"
                      value={item.newMedName}
                      onChange={(e) =>
                        handleItemChange(index, "newMedName", e.target.value)
                      }
                      required
                    />
                  </div>
                )}
                <input
                  className="w-full sm:w-1/4 p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                  type="number"
                  placeholder="Quantity"
                  value={item.qty}
                  onChange={(e) =>
                    handleItemChange(index, "qty", e.target.value)
                  }
                  required
                />
                <input
                  className="w-full sm:w-1/4 p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                  type="number"
                  placeholder="MRP"
                  value={item.mrp}
                  onChange={(e) =>
                    handleItemChange(index, "mrp", e.target.value)
                  }
                  required
                />
                <input
                  className="w-full sm:w-1/4 p-2 sm:p-3 border border-gray-300 rounded-md text-lg mb-2 sm:mb-0"
                  type="number"
                  placeholder="Final Price"
                  value={item.finalPrice}
                  onChange={(e) =>
                    handleItemChange(index, "finalPrice", e.target.value)
                  }
                  required
                />
                <input
                  className="w-full sm:w-1/6 p-2 sm:p-3 border border-gray-300 rounded-md text-lg"
                  type="number"
                  placeholder="Total Price"
                  value={item.total.toFixed(2)}
                  readOnly
                />
                {medicineItems.length > 1 && (
                  <button
                    type="button"
                    className="p-2 sm:p-3 bg-red-500 text-white rounded-md text-lg mt-2 sm:mt-0"
                    onClick={() => handleRemoveItem(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
              {index === medicineItems.length - 1 && (
                <button
                  type="button"
                  className="w-full p-2 sm:p-3 bg-green-500 text-white rounded-md text-lg"
                  onClick={handleAddItem}
                >
                  Add Medicine
                </button>
              )}
            </div>
          ))}
          {error && (
            <div className="mb-4 sm:mb-6 text-red-600 text-lg font-medium">
              {error}
            </div>
          )}
          <div className="mb-4 sm:mb-6 text-xl sm:text-2xl font-bold">
            Total Amount: ${totalAmount.toFixed(2)}
          </div>
          <button
            className="w-full bg-green-700 text-white px-4 py-2 sm:py-3 rounded-md text-lg hover:bg-blue-600"
            type="submit"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewPurchase;
